import {
  indexCommissions,
  updateHouseDeductionAmounts,
  updateInternalCommissions,
  updateOutsideCommissions,
  updateSplitCommissions
} from './commissions';

import { updateDeductions } from './deductions';
import { isStandalone } from 'bundles/backoffice/vouchers/helpers/listings';

export function updateVoucher(
  voucher,
  {
    accessibleSplitUserIds,
    updateGrossCommissionPercent
  } = {}
) {
  voucher = { ...voucher, totalDeduction: 0, totalHouseDeduction: 0 };

  if (voucher.hasTransactionSides) {
    updateTransactionSideGrossCommissionValues(voucher, updateGrossCommissionPercent);
  } else {
    updateGrossCommissionValues(voucher, updateGrossCommissionPercent);
  }

  voucher.receivables = voucher.receivables.map((receivable, index) => {
    return { ...receivable, index };
  });

  indexCommissions(voucher);
  updateOutsideCommissions(voucher);

  updateDeductions(voucher);
  for (const deduction of voucher.deductions) {
    if (!deduction._destroy) {
      const key = deduction.deductFromHouse ? 'totalHouseDeduction' : 'totalDeduction';
      voucher[key] += deduction.amount;
    }
  }

  updateInternalCommissions(voucher);
  updateSplitCommissions(voucher, { accessibleSplitUserIds });
  updateHouseDeductionAmounts(voucher);

  return voucher;
}

export function updateTransactionSideGrossCommissionValues(voucher, updatePercent) {
  voucher.transactionSideDetails = voucher.transactionSideDetails.map((details) => {
    const updatedDetails = { ...details };
    if (updatePercent) {
      updatedDetails.grossPercent = (
        voucher.transactionValue === 0 ? 0 : details.grossAmount / voucher.transactionValue * 100
      );
    } else {
      updatedDetails.grossPercent = Math.max(0.0, Math.min(100.0, details.grossPercent));

      updatedDetails.grossAmount = (
        Math.round(voucher.transactionValue * updatedDetails.grossPercent) / 100
      );
    }

    return updatedDetails;
  });

  updateGrossCommissionValuesFromTransactionSide(voucher);
}

function updateGrossCommissionValuesFromTransactionSide(voucher) {
  voucher.grossCommissionAmount = voucher.transactionSideDetails.reduce(
    (sum, details) => sum + details.grossAmount, 0
  );
  voucher.grossCommissionPercent = voucher.transactionSideDetails.reduce(
    (sum, details) => sum + details.grossPercent, 0
  );
  voucher.grossCommissionUnit = 'gross_commission_unit_dollars';
}

function updateGrossCommissionValues(voucher, updateGrossCommissionPercent) {
  if (voucher.grossCommissionPercent == null || updateGrossCommissionPercent) {
    if (updateGrossCommissionPercent) {
      if (voucher.transactionValue === 0) {
        voucher.grossCommissionPercent = 0;
      } else {
        voucher.grossCommissionPercent = voucher.grossCommissionAmount / voucher.transactionValue * 100;
      }
    } else {
      voucher.grossCommissionAmount = null;
    }
  } else {
    voucher.grossCommissionPercent = Math.max(0.0, Math.min(100.0, voucher.grossCommissionPercent));

    if (voucher.transactionValue === 0) {
      if (isStandalone(voucher)) {
        // For standalone vouchers, having a GCA with no transaction value is permitted
      } else {
        voucher.grossCommissionAmount = 0;
      }
    } else if (voucher.grossCommissionUnit === 'gross_commission_unit_percent') {
      voucher.grossCommissionAmount = Math.round(voucher.transactionValue * voucher.grossCommissionPercent) / 100;
    }
  }
}

export function getListingTypeLabel(listingType) {
  switch (listingType) {
  case 'SaleListing':
    return 'Sale';
  case 'LeaseSpaceListing':
    return 'Lease';
  default:
    return 'Deal';
  }
}

export function getPayerContactRoles(voucher) {
  return voucher.contactRoles.filter(role => role.role === 'payer' && !role._destroy);
}

export function getPayerContacts(voucher) {
  return getPayerContactRoles(voucher).map(role => role.contact);
}

export function getGrossCommissionValues(voucher, transactionSide) {
  if (!transactionSide) {
    return {
      grossAmount: voucher.grossCommissionAmount || 0,
      grossPercent: voucher.grossCommissionPercent || 0,
      grossUnit: voucher.grossCommissionUnit,
    };
  }

  const transactionSideDetails = voucher.transactionSideDetails.find(
    details => !details._destroy && details.transactionSide === transactionSide
  );

  if (!transactionSideDetails.grossPercent) {
    transactionSideDetails.grossPercent = 0;
  }

  return transactionSideDetails;
}

export function getPeopleToLink(voucher) {
  const allUnsynced = [
    ...getUnlinkedContactsFromReceivables(voucher),
    ...getUnlinkedUsersFromCommissions(voucher),
    ...getUnlinkedContactsFromCommissions(voucher),
  ];
  const people = [];
  const existingKeys = new Set();
  allUnsynced.forEach((person) => {
    const key = `${person.type}:${person.id}:${person.relate_to}`;
    if (!existingKeys.has(key)) {
      existingKeys.add(key);
      people.push(person);
    }
  });
  return people;
}

function getUnlinkedContactsFromReceivables(voucher) {
  const people = [];

  (voucher?.receivables || []).forEach((receivable) => {
    const payer = receivable.payer;
    if (!payer) return;
    if (payer.quickbooksCustomerId) return;

    people.push({
      type: 'contact',
      relate_to: 'customer',
      id: payer.id,
      name: payer.name,
      object: payer
    });
  });

  return people;
}

function getUnlinkedUsersFromCommissions(voucher) {
  const people = [];

  (voucher?.commissions || []).forEach((commission) => {
    const broker = commission.broker;

    if (!broker) return;
    if (broker.quickbooksId) return;
    people.push({
      type: 'user',
      relate_to: 'vendor',
      id: broker.id,
      name: broker.name,
      object: broker
    });
  });

  return people;
}

function getUnlinkedContactsFromCommissions(voucher) {
  const people = [];

  (voucher?.commissions || []).forEach((commission) => {
    const contact = commission.contact;

    if (!contact) return;
    if (contact.quickbooksVendorId) return;

    people.push({
      type: 'contact',
      relate_to: 'vendor',
      id: contact.id,
      name: contact.name,
      object: contact
    });
  });

  return people;
}

